import { MouseEventHandler, useEffect, useRef, useState } from 'react'

import { Drawer, typography } from '@joor/design-system'
import { useSelector } from 'react-redux'
import styled from 'styled-components/macro'

import { OrderStatus } from '__generated__/atlas-types'

import {
  getAccountProfileName,
  isAccountTypeBrand,
  isEditApprovedShippedRestricted as isEditApprovedShippedRestrictedSelector,
} from 'store/currentUser/selectors'

import { ReviewPageIds } from '../../review.ids'
import { useUpdateOrderInfo } from '../../review.mutations'
import { getOrderSeparatedComments } from '../../utils/review.utils'
import { OrderData } from '../Overview/Overview'
import { MessagesForm, MessagesList, OrderMessage } from './Messages'

const EOP = '<eop>'

const getRawCommentForMutation = (
  accountProfileName: string,
  message: string,
) => `\n${EOP}${accountProfileName}: ${message}${EOP}`

const TextAreaWrapper = styled.div`
  width: 100%;
  height: 100%;
  border: 1px solid var(--color-primary-400);
  border-radius: var(--size-border-radius-medium);

  &:hover {
    border: 1px solid var(--color-primary-800);
  }
  &:focus-within {
    border: 1px solid var(--color-secondary-800);
  }
`

const StyledTextArea = styled.textarea`
  width: 100%;
  height: 100%;
  padding: 6px 12px;
  resize: none;
  border: none;
  border-radius: var(--size-border-radius-medium);
  color: var(--primary-800, #323e48);
  font-family: Montserrat, sans-serif;
  font-size: 12px !important;
  font-style: normal;
  font-weight: 500;
  line-height: 16px !important;
  letter-spacing: 0.6px;

  :focus {
    outline: none;
  }

  ::placeholder {
    ${typography.alpha.primaryInactive.body3};
  }

  ::selection {
    background-color: var(--color-secondary-200);
  }
`

const EditBtnLabels = {
  Edit: 'Edit',
  Save: 'Save',
} as const

export const MessageDrawerTestIds = {
  TextArea: 'message-drawer-textarea',
} as const

export const MessagesDrawer = ({
  id,
  status,
  retailerProfile,
  dataMessages,
  loading,
}: {
  id: string
  status?: OrderStatus
  retailerProfile?: NonNullable<OrderData['retailerProfile']>
  dataMessages: OrderData['orderComments']
  loading?: boolean
}): JSX.Element => {
  const { onUpdate, isUpdating } = useUpdateOrderInfo()
  const accountProfileName = useSelector(getAccountProfileName)
  const isBrandAccount = useSelector(isAccountTypeBrand)
  const isEditApprovedShippedRestricted = useSelector(
    isEditApprovedShippedRestrictedSelector,
  )

  const [rawOrderComments, setRawOrderComments] = useState('')
  const [isEditingComment, setIsEditingComment] = useState(false)

  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const handleEditAction = () => {
    setIsEditingComment((state) => !state)
    if (textareaRef.current && isEditingComment) {
      const value = textareaRef.current.value
      const previousValue = rawOrderComments
        .replaceAll(EOP, '\n')
        .replace(/(\n)\1/gm, '$1')
      if (value === previousValue || !id) {
        return
      }

      const updatedRawComment = value.replaceAll('\n', EOP).concat(EOP)
      setRawOrderComments(updatedRawComment)
      onUpdate({
        orderIds: [id],
        orderDetails: { orderComments: updatedRawComment },
      })
    }
  }

  const allowedEditableStatuses = [
    OrderStatus.NOTES,
    OrderStatus.PENDING,
    OrderStatus.APPROVED,
  ]

  const isApprovedEditRestricted =
    status === OrderStatus.APPROVED && isEditApprovedShippedRestricted

  const canEditComments =
    !loading &&
    isBrandAccount &&
    allowedEditableStatuses.includes(status as OrderStatus) &&
    !isApprovedEditRestricted

  const editAction:
    | {
        id: string
        label: string
        handler: MouseEventHandler<HTMLButtonElement>
      }
    | undefined = canEditComments
    ? {
        id: isEditingComment
          ? ReviewPageIds.CommentsSaveId
          : ReviewPageIds.CommentsEditId,
        label: isEditingComment ? EditBtnLabels.Save : EditBtnLabels.Edit,
        handler: handleEditAction,
      }
    : undefined
  useEffect(() => {
    if (!dataMessages) {
      return
    }
    setRawOrderComments(dataMessages)
  }, [dataMessages])

  const messages = getOrderSeparatedComments(rawOrderComments)
    .filter(({ message, prefix }) => message !== '\n' && prefix)
    .map((message, index) =>
      isBrandAccount && message.prefix !== accountProfileName && index !== 0
        ? { ...message, prefix: retailerProfile?.name || '' }
        : message,
    )

  const orderComments: OrderMessage[] = getOrderSeparatedComments(
    rawOrderComments,
  ).filter((comment) => comment.message !== '\n' && !comment.prefix)

  const handleAddNewMessage = (message: string): void => {
    if (!id || !message.length) {
      return
    }
    const updatedRawComments = rawOrderComments.concat(
      getRawCommentForMutation(accountProfileName, message),
    )
    setRawOrderComments(updatedRawComments)
    onUpdate({
      orderIds: [id],
      orderDetails: { orderComments: updatedRawComments },
    })
  }

  return (
    <>
      <Drawer.Header className={editAction?.id} extraAction={editAction}>
        Comments
      </Drawer.Header>
      <Drawer.Content>
        {isEditingComment ? (
          <TextAreaWrapper>
            <StyledTextArea
              data-testid={MessageDrawerTestIds.TextArea}
              ref={textareaRef}
              defaultValue={rawOrderComments
                .replaceAll(EOP, '\n')
                .replace(/(\n)\1/gm, '$1')}
              autoFocus
              onFocus={(event) => {
                event.target.selectionStart = event.target.value.length
              }}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  setIsEditingComment(false)
                }
              }}
            />
          </TextAreaWrapper>
        ) : (
          <MessagesList
            orderComments={orderComments}
            messages={messages}
            loading={loading}
          />
        )}
      </Drawer.Content>
      <Drawer.Footer>
        <MessagesForm
          onSubmitNewMessage={handleAddNewMessage}
          disable={isUpdating || isEditingComment}
        />
      </Drawer.Footer>
    </>
  )
}
